import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useApiClient } from '../../lib/api';
import { useNotification } from '../../providers/notification-provider';

const DAYS = [
  { value: '0', label: 'Domingo' },
  { value: '1', label: 'Segunda-feira' },
  { value: '2', label: 'Terça-feira' },
  { value: '3', label: 'Quarta-feira' },
  { value: '4', label: 'Quinta-feira' },
  { value: '5', label: 'Sexta-feira' },
  { value: '6', label: 'Sábado' },
];

const DAY_NAME_TO_VALUE = {
  Sunday: '0',
  Monday: '1',
  Tuesday: '2',
  Wednesday: '3',
  Thursday: '4',
  Friday: '5',
  Saturday: '6',
};

function normalizeTime(time) {
  if (!time) return '';
  return String(time).slice(0, 5);
}

function timeToMinutes(time) {
  const [hh, mm] = String(time).split(':').map(Number);
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  return hh * 60 + mm;
}

function formatHoursForApi(hours) {
  return hours.map((hour) => ({
    dayOfWeek: parseInt(hour.dayOfWeek, 10),
    openTime: `${normalizeTime(hour.openTime)}:00`,
    closeTime: `${normalizeTime(hour.closeTime)}:00`,
  }));
}

function buildValidationError(hours, closures) {
  const duplicateClosures = new Set();
  for (const closure of closures) {
    if (!closure.date) {
      return 'Toda exceção precisa de uma data válida.';
    }

    if (duplicateClosures.has(closure.date)) {
      return `A data ${closure.date} está duplicada na lista de exceções.`;
    }
    duplicateClosures.add(closure.date);
  }

  const duplicates = new Set();
  const byDay = new Map();

  for (const hour of hours) {
    const open = normalizeTime(hour.openTime);
    const close = normalizeTime(hour.closeTime);

    if (!open || !close) {
      return 'Preencha abertura e fechamento em todos os turnos.';
    }

    const key = `${hour.dayOfWeek}:${open}:${close}`;
    if (duplicates.has(key)) {
      const dayName = DAYS.find((day) => day.value === String(hour.dayOfWeek))?.label ?? 'dia';
      return `Há turno duplicado em ${dayName} (${open}-${close}).`;
    }
    duplicates.add(key);

    const openMinutes = timeToMinutes(open);
    const closeMinutes = timeToMinutes(close);
    if (openMinutes === null || closeMinutes === null) {
      return 'Horário inválido encontrado na agenda.';
    }

    if (openMinutes === closeMinutes) {
      const dayName = DAYS.find((day) => day.value === String(hour.dayOfWeek))?.label ?? 'dia';
      return `Abertura e fechamento não podem ser iguais em ${dayName}.`;
    }

    const dayKey = String(hour.dayOfWeek);
    const list = byDay.get(dayKey) || [];

    if (closeMinutes > openMinutes) {
      list.push({ start: openMinutes, end: closeMinutes, overnight: false });
    } else {
      list.push({ start: openMinutes, end: 24 * 60, overnight: true });
      const nextDay = String((parseInt(dayKey, 10) + 1) % 7);
      const nextList = byDay.get(nextDay) || [];
      nextList.push({ start: 0, end: closeMinutes, overnight: true });
      byDay.set(nextDay, nextList);
    }

    byDay.set(dayKey, list);
  }

  for (const day of DAYS) {
    const list = byDay.get(day.value) || [];
    const sorted = [...list].sort((a, b) => a.start - b.start);
    for (let i = 1; i < sorted.length; i++) {
      const previous = sorted[i - 1];
      const current = sorted[i];
      if (current.start < previous.end) {
        return `Existem turnos sobrepostos em ${day.label}. Ajuste os intervalos.`;
      }
    }
  }

  return null;
}

const OperatingHoursModal = ({ restaurantId, companyId, isOpen, onClose }) => {
  const api = useApiClient();
  const { showToast } = useNotification();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const { register, control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      hours: [],
      closureDates: [],
    },
  });

  const { fields: hourFields, append: appendHour, remove: removeHour } = useFieldArray({
    control,
    name: 'hours',
  });

  const { fields: closureFields, append: appendClosure, remove: removeClosure } = useFieldArray({
    control,
    name: 'closureDates',
  });

  const watchedHours = watch('hours');

  const groupedHours = useMemo(() => {
    const groups = new Map(DAYS.map((day) => [day.value, []]));

    const safeHours = Array.isArray(watchedHours) ? watchedHours : [];

    hourFields.forEach((field, index) => {
      const value = safeHours[index] || {};
      const dayOfWeek = String(value.dayOfWeek ?? field.dayOfWeek ?? '1');
      const list = groups.get(dayOfWeek) || [];
      list.push({
        index,
        openTime: normalizeTime(value.openTime ?? field.openTime),
        closeTime: normalizeTime(value.closeTime ?? field.closeTime),
      });
      groups.set(dayOfWeek, list);
    });

    return groups;
  }, [hourFields, watchedHours]);

  useEffect(() => {
    if (!isOpen || !restaurantId || !companyId) {
      return;
    }

    setLoading(true);
    setError(null);

    api
      .get(`/api/v1/restaurants/${restaurantId}/operating-hours`, {
        params: { companyId }
      })
      .then((response) => {
        const payload = response.data || { hours: [], closureDates: [] };
        const normalizedHours = (payload.hours || [])
          .map((hour) => ({
            dayOfWeek: DAY_NAME_TO_VALUE[hour.dayOfWeek] || String(hour.dayOfWeek),
            openTime: normalizeTime(hour.openTime),
            closeTime: normalizeTime(hour.closeTime),
          }))
          .sort((a, b) => {
            const dayDiff = parseInt(a.dayOfWeek, 10) - parseInt(b.dayOfWeek, 10);
            if (dayDiff !== 0) return dayDiff;
            return a.openTime.localeCompare(b.openTime);
          });

        reset({
          hours: normalizedHours,
          closureDates: payload.closureDates || [],
        });
      })
      .catch((requestError) => {
        console.error(requestError);
        setError('Não foi possível carregar os horários de funcionamento.');
      })
      .finally(() => setLoading(false));
  }, [api, companyId, isOpen, restaurantId, reset]);

  const handleAddShift = (dayOfWeek) => {
    appendHour({ dayOfWeek, openTime: '09:00', closeTime: '18:00' });
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setError(null);

    try {
      const validationError = buildValidationError(data.hours || [], data.closureDates || []);
      if (validationError) {
        setError(validationError);
        return;
      }

      await api.put(`/api/v1/restaurants/${restaurantId}/operating-hours`, {
        companyId,
        hours: formatHoursForApi(data.hours || []),
        closureDates: data.closureDates || [],
      });

      showToast('Horários de funcionamento atualizados com sucesso.', 'success');
      onClose();
    } catch (requestError) {
      console.error(requestError);
      const message = requestError?.response?.data?.message || requestError?.response?.data || 'Erro ao salvar horários de funcionamento.';
      setError(typeof message === 'string' ? message : 'Erro ao salvar horários de funcionamento.');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/40 p-4">
      <div className="mx-auto my-4 w-full max-w-5xl rounded-2xl bg-white p-4 shadow-2xl sm:p-6">
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 pb-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Horário de funcionamento</h3>
            <p className="mt-1 text-sm text-slate-600">
              Configure turnos por dia da semana, incluindo horários quebrados (ex.: 11:00-15:00 e 18:00-22:00).
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-1.5 text-sm font-semibold text-slate-600 hover:bg-slate-100"
          >
            Fechar
          </button>
        </div>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Carregando horários...</div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 pt-4">
            {error ? (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
            ) : null}

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Agenda semanal</h4>
              </div>

              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                {DAYS.map((day) => {
                  const shifts = groupedHours.get(day.value) || [];
                  return (
                    <article key={day.value} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <h5 className="text-sm font-semibold text-slate-800">{day.label}</h5>
                        <button
                          type="button"
                          onClick={() => handleAddShift(day.value)}
                          className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                        >
                          + Turno
                        </button>
                      </div>

                      {shifts.length === 0 ? (
                        <p className="text-xs text-slate-500">Sem turnos configurados.</p>
                      ) : (
                        <div className="space-y-2">
                          {shifts.map((shift) => (
                            <div key={shift.index} className="grid grid-cols-1 gap-2 rounded-md border border-slate-200 bg-white p-2 sm:grid-cols-[1fr_auto_1fr_auto] sm:items-center">
                              <input
                                type="hidden"
                                {...register(`hours.${shift.index}.dayOfWeek`)}
                                value={day.value}
                              />
                              <input
                                type="time"
                                {...register(`hours.${shift.index}.openTime`)}
                                className="rounded-md border-slate-300 text-sm"
                                required
                              />
                              <span className="text-center text-xs font-semibold text-slate-500">até</span>
                              <input
                                type="time"
                                {...register(`hours.${shift.index}.closeTime`)}
                                className="rounded-md border-slate-300 text-sm"
                                required
                              />
                              <button
                                type="button"
                                onClick={() => removeHour(shift.index)}
                                className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                              >
                                Remover
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            </section>

            <section className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold uppercase tracking-wide text-slate-700">Exceções por data</h4>
                <button
                  type="button"
                  onClick={() => appendClosure({ date: '', reason: '', isClosed: true })}
                  className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-50"
                >
                  + Exceção
                </button>
              </div>

              {closureFields.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  Nenhuma exceção cadastrada.
                </p>
              ) : (
                <div className="space-y-2">
                  {closureFields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-1 gap-2 rounded-lg border border-slate-200 bg-white p-3 md:grid-cols-[160px_1fr_auto_auto] md:items-center">
                      <input
                        type="date"
                        {...register(`closureDates.${index}.date`)}
                        className="rounded-md border-slate-300 text-sm"
                        required
                      />
                      <input
                        type="text"
                        {...register(`closureDates.${index}.reason`)}
                        placeholder="Motivo (opcional)"
                        className="rounded-md border-slate-300 text-sm"
                      />
                      <label className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700">
                        <input type="checkbox" {...register(`closureDates.${index}.isClosed`)} className="h-4 w-4 rounded border-slate-300 text-indigo-600" />
                        Fechado
                      </label>
                      <button
                        type="button"
                        onClick={() => removeClosure(index)}
                        className="rounded-md border border-rose-200 px-2.5 py-1 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                      >
                        Remover
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-slate-700 ring-1 ring-inset ring-slate-300 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-60"
              >
                {saving ? 'Salvando...' : 'Salvar horários'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default OperatingHoursModal;
